import { useState } from 'react';
import { api } from '../api';
import type { User } from '../api';
import { Link } from '../router';
import { useSession } from '../session';
import { Shell } from '../components/Shell';
import { Loading } from '../components/bits';

/** Your details, on their own page, because you change them about once. */
export function Settings() {
  const { t, user, refresh } = useSession();
  if (!user) return <Shell><Loading /></Shell>;

  return (
    <Shell width="narrow">
      <div className="page-head">
        <div className="grow">
          <p className="kicker">{user.email}</p>
          <h1>{t.settings}</h1>
        </div>
      </div>
      <Form user={user} onSaved={refresh} />
      <p style={{ marginTop: 'var(--sp-6)' }}>
        <Link to="/profile" className="link">&lt; {t.backToOverview}</Link>
      </p>
    </Shell>
  );
}

function Form({ user, onSaved }: { user: User; onSaved: () => Promise<void> }) {
  const { t, theme, setTheme } = useSession();
  const [form, setForm] = useState({
    name: user.name,
    jobTitle: user.jobTitle ?? '',
    notifyPref: user.notifyPref,
    locale: user.locale,
    timezone: user.timezone,
  });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      await api.updateMe(form);
      await onSaved();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const zones = Intl.supportedValuesOf?.('timeZone') ?? [user.timezone];

  return (
    <form onSubmit={save} className="stack">
      <div>
        <label htmlFor="name">{t.name}</label>
        <input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </div>
      <div>
        <label htmlFor="job">{t.jobTitle}</label>
        <input id="job" value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} />
      </div>
      <div>
        <label htmlFor="locale">{t.language}</label>
        <select
          id="locale"
          value={form.locale}
          onChange={(e) => setForm({ ...form, locale: e.target.value as 'en' | 'pt' })}
        >
          <option value="en">English</option>
          <option value="pt">Português</option>
        </select>
      </div>
      <div>
        <label htmlFor="tz">{t.timezone}</label>
        <select id="tz" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}>
          {zones.map((z) => <option key={z} value={z}>{z}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor="notify">{t.notifications}</label>
        <select
          id="notify"
          value={form.notifyPref}
          onChange={(e) => setForm({ ...form, notifyPref: e.target.value as 'all' | 'digest' | 'none' })}
        >
          <option value="all">{t.notifyAll}</option>
          <option value="digest">{t.notifyDigest}</option>
          <option value="none">{t.notifyNone}</option>
        </select>
      </div>
      <div>
        <label htmlFor="theme">{t.theme}</label>
        <select
          id="theme"
          value={theme}
          onChange={(e) => setTheme(e.target.value as 'dark' | 'light')}
        >
          <option value="dark">{t.dark}</option>
          <option value="light">{t.light}</option>
        </select>
      </div>
      {problem && <p className="error" role="alert">{problem}</p>}
      <div className="row">
        <button className="primary" type="submit" disabled={busy}>{t.save} &gt;</button>
        {saved && <span className="muted">{t.saved}</span>}
      </div>
    </form>
  );
}
